import React, { useState, useEffect } from 'react';
import LeaseApiService from '../../services/apiService';
import { FaSave, FaChevronLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getName } from '../../../Redux/selecters';
import CommunityDetails from './communityDetails';
import { fetchCommunityCountries, fetchCommunityCitiesByCountry } from './communityApiHelpers';

const emptyForm = () => ({
  communityNo: '',
  descriptionEn: '',
  descriptionAr: '',
  country: null,
  city: null,
  marketingDescription: '',
  remarks: '',
});

const CreateCommunity = ({ onClose, onSaved } = {}) => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const navigate = useNavigate();
  const userName = useSelector(getName);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [countriesList, setCountriesList] = useState([]);
  const [cityList, setCityList] = useState([]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const countries = await fetchCommunityCountries(apiService);
      setCountriesList(countries);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const handleBackClick = () => {
    if (onClose) {
      onClose();
      return;
    }
    window.history.back();
  };

  const handleCountryChange = async (selectedOption) => {
    setFormData((prev) => ({ ...prev, country: selectedOption, city: null }));
    if (!selectedOption?.label) {
      setCityList([]);
      return;
    }
    try {
      const cities = await fetchCommunityCitiesByCountry(apiService, selectedOption.label);
      setCityList(cities);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCityList([]);
    }
  };

  const buildPayload = (currentDatetimeUTC) => ({
    community: {
      // Matches the Swagger schema (record_Id, numeric 0 for a new record) -
      // and updateCommunity.js's own Number(...) convention for this field.
      record_Id: 0,
      communityNo: formData.communityNo || '0',
      description: formData.descriptionEn || '0',
      descriptionBI: formData.descriptionAr || '0',
      city: formData.city?.label || '0',
      country: formData.country?.label || '0',
      marketingDescription: formData.marketingDescription || '0',
      remarks: formData.remarks || '0',
      createdBy: userName || '',
      createdOn: currentDatetimeUTC,
      modifiedBy: userName || '',
      modifiedOn: currentDatetimeUTC,
    },
  });

  const handleSave = async () => {
    // Community No isn't user-entered here - CommunityRepository.CreateCommunity
    // always recomputes it server-side as the next sequential "COM-N" and
    // never reads whatever's sent in this field.
    if (!formData.descriptionEn?.trim()) {
      toast.warning(t('communityMaster.validation.descriptionRequired'));
      return;
    }
    if (!formData.country) {
      toast.warning(t('communityMaster.validation.countryRequired'));
      return;
    }
    if (!formData.city) {
      toast.warning(t('communityMaster.validation.cityRequired'));
      return;
    }

    try {
      setLoading(true);
      const currentDatetimeUTC = new Date().toISOString();
      const body = buildPayload(currentDatetimeUTC);
      const res = await apiService.post('POST_COMMUNITY', body);
      if (res?.data?.isSuccessful || res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
        toast.success(res?.data?.message || t('communityMaster.createSuccess'));
        setLoading(false);
        if (onSaved) {
          onSaved();
        } else {
          navigate('/Customer/Property/Master/Community/Listing');
        }
      } else {
        toast.warning(res?.data?.message || t('communityMaster.createFailed'));
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating community:', error);
      toast.error(t('communityMaster.createFailed'));
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column customer-modal-form" style={{ height: '85vh' }}>
      <div className="p-3 border-bottom d-flex align-items-center justify-content-between flex-shrink-0">
        <h5 className="mb-0">{t('communityMaster.addTitle')}</h5>
        <button type="button" className="btn-close" aria-label="Close" onClick={handleBackClick}></button>
      </div>
      <div className="p-3" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <CommunityDetails formData={formData} setFormData={setFormData} countriesList={countriesList} cityList={cityList} onCountryChange={handleCountryChange} isNew />
      </div>
      <div className="p-3 border-top d-flex justify-content-start gap-3 flex-shrink-0">
        {loading ? (
          <div className="mainbtn flex-end gap-2">
            <ClipLoader color="#ffffff" className="mt-1" size={20} />
          </div>
        ) : (
          <div className="mainbtn flex-end gap-2" onClick={handleSave}>
            <FaSave color="#fff" className="center mt-1 mx-2" />
            <div className="center">{t('common.save')}</div>
          </div>
        )}
        <div className="mainbtnRed flex-end gap-2" onClick={handleBackClick}>
          <FaChevronLeft color="#fff" className="center mt-1 mx-2" />
          <div className="center">{t('common.cancel')}</div>
        </div>
      </div>
    </div>
  );
};

export default CreateCommunity;
